import type {FC} from "react";
import {memo, useMemo} from "react";
import {useDropzone} from "react-dropzone";

import imageUploadingPlaceholderUrl from "../../assets/image-uploading-placeholder.png";
import {Box} from "../ui/box";
import {Center} from "../ui/center";
import {Image} from "../ui/image";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

import type {UploadImageAreaProps} from "./types";

export type Props = Pick<UploadImageAreaProps, "noDrag" | "showDropzoneOverlay" | "maxImages"> & {
    onDrop: (files: File[]) => void;
    children: (params: {open: () => void; isDragAccept: boolean}) => React.ReactNode;
    className?: string;
};

const Dropzone: FC<Props> = ({
    className,
    maxImages = 5,
    noDrag,
    showDropzoneOverlay,
    children,
    onDrop
}) => {
    const {getRootProps, getInputProps, open, isDragAccept} = useDropzone({
        accept: {"image/jpeg": [], "image/png": [], "image/gif": [], "image/tiff": []},
        onDrop,
        multiple: maxImages > 1,
        maxFiles: maxImages,
        noClick: true,
        noKeyboard: true,
        noDrag
    });

    const imageUploadingPlaceholderSource = useMemo(
        () =>
            typeof imageUploadingPlaceholderUrl === "number"
                ? imageUploadingPlaceholderUrl
                : {
                      uri: imageUploadingPlaceholderUrl
                  },
        []
    );

    return (
        <div
            className={className}
            data-testid="dropzone"
            style={{display: "flex", flex: 1}}
            {...getRootProps()}
        >
            <Box className="flex-1">
                {children({open, isDragAccept})}
                <input {...getInputProps()} />
                {showDropzoneOverlay && isDragAccept && (
                    <>
                        <div
                            className={`
                                z-20 
                                inset-0 
                                absolute 
                                bg-white/50 
                                pointer-events-none 
                                backdrop-blur-md
                                rounded-xl
                            `}
                        />
                        <Center className="absolute inset-[30px] border border-dashed border-primary-600 rounded-xl z-30">
                            <VStack className="items-center justify-center" space="2xl">
                                <Image
                                    alt="Empty image"
                                    className="w-[152px] h-[152px]"
                                    source={imageUploadingPlaceholderSource}
                                />
                                <Text color="blueGray.800" textAlign="center" variant="xl">
                                    Drag & Drop Image Here
                                </Text>
                            </VStack>
                        </Center>
                    </>
                )}
            </Box>
        </div>
    );
};

export default memo(Dropzone);
